import classNames from 'classnames';
import { format } from 'date-fns';
import { cs, sk, enUS } from 'date-fns/locale';
import FocusTrap from 'focus-trap-react';
import React, { useRef, useState } from 'react';
import { FormControl, FormControlProps } from 'react-bootstrap';
import { DayPicker, DayPickerSingleProps, SelectSingleEventHandler } from 'react-day-picker';
import { usePopper } from 'react-popper';
import { Button } from '../../Button';
import { DatepickerCaption } from '../../Datepicker/DatepickerCaption';
import { Icon, IconProps } from '../../Icon';
import { InputGroup } from '../../InputGroup';
import { Typography } from '../../Typography';
import styles from './Datepicker.module.scss';
import {LocaleType, LocaleEnum} from 'src/types/general';
import {Form} from "@components/Form";

const locales = {
    [LocaleEnum.cs]: cs,
    [LocaleEnum.sk]: sk,
    [LocaleEnum.en]: enUS,
};

export type FormDatepickerProps = Omit<DayPickerSingleProps, 'locale' | 'mode' | 'onSelect'> & {
	locale?: LocaleType;
	onSelect: SelectSingleEventHandler;
	inputProps?: FormControlProps;
	iconName?: IconProps['name'] | false;
    errors?: string;
	dateFormat?: string;
};

export const FormDatepicker = ({
	locale = LocaleEnum.cs,
	inputProps = {},
	iconName = 'Calendar',
	dateFormat = 'd. M. yyyy',
	selected,
	onSelect,
    errors,
	...props
}: FormDatepickerProps) => {
	const usedLocale = locales[locale];

	const [isPopperOpen, setIsPopperOpen] = useState(false);

	const popperRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(null);

	const popper = usePopper(popperRef.current, popperElement, {
		placement: 'bottom-start',
	});

	const inputValue = selected ? format(selected, dateFormat, { locale: usedLocale }) : '';

	const closeDialog = () => {
		setIsPopperOpen(false);
	};

	const openDialog = () => {
		setIsPopperOpen(true);
	};

	const handleDaySelect: SelectSingleEventHandler = (...rest) => {
		onSelect(...rest);

		closeDialog();
	};

	return (
		<>
			<InputGroup ref={popperRef} size={inputProps.size} hasValidation={!!errors}>
				<FormControl
					{...inputProps}
					ref={inputRef}
					key={inputValue}
					defaultValue={inputValue}
					onFocus={(event) => {
						openDialog();

						inputProps?.onFocus?.(event);
					}}
					className={classNames(styles.datepickerInput, inputProps.className, {['is-invalid']: !!errors})}
					readOnly
				/>

				{iconName && (
					<InputGroup.Text>
						<Button variant="blank" onClick={() => inputRef.current?.focus()} tabIndex={-1}>
							<Typography color="grey-dark">
								<Icon name={iconName} size={inputProps.size} />
							</Typography>
						</Button>
					</InputGroup.Text>
				)}

                {errors && <Form.Control.Feedback type={'invalid'}>{errors}</Form.Control.Feedback>}
			</InputGroup>

			{isPopperOpen && (
				<FocusTrap
					active
					focusTrapOptions={{
						initialFocus: false,
						allowOutsideClick: true,
						clickOutsideDeactivates: true,
						onDeactivate: closeDialog,
					}}
				>
					<div
						tabIndex={-1}
						style={popper.styles.popper}
						className={classNames(styles.datepicker, styles.datepickerDialog)}
						{...popper.attributes.popper}
						ref={setPopperElement}
						role="dialog"
					>
						<div className={styles.datepickerHolder}>
							<DayPicker
								{...props}
								initialFocus={isPopperOpen}
								mode="single"
								selected={selected}
								onSelect={handleDaySelect}
								locale={usedLocale}
								classNames={{
									root: styles.datepickerRoot,
									vhidden: 'visually-hidden',
									button_reset: styles.datepickerButtonReset,

									caption: styles.datepickerCaption,
									nav: styles.datepickerNav,
									nav_button: styles.datepickerNavButton,
									nav_button_previous: styles.datepickerNavButtonPrev,
									nav_button_next: styles.datepickerNavButtonNext,

									months: styles.datepickerMonths,
									month: styles.datepickerMonth,

									table: styles.datepickerTable,
									head: styles.datepickerHead,
									head_cell: styles.datepickerHeadCell,
									cell: styles.datepickerCell,
									day: styles.datepickerDay,
									day_today: styles.datepickerDayToday,
									day_outside: styles.datepickerDayOutside,
									day_disabled: styles.datepickerDayDisabled,
									day_selected: styles.datepickerDaySelected,
								}}
								components={{ Caption: DatepickerCaption }}
							/>
						</div>
					</div>
				</FocusTrap>
			)}
		</>
	);
};
